import { CopyComponentRenderer, SitemapForm, SitemapVersionCopyProps, SitemapVersionForm } from "@iventis/sitemaps";
import { SitemapRoutingContext } from "@iventis/sitemaps/src/context/sitemap-routing-context";
import { SitemapApiContext, ISitemapApiContext } from "@iventis/sitemaps/src/context/sitemap-api-context";
import { SitemapVersionLevelForm } from "@iventis/sitemaps/src/sitemap-version-level-form";
import { Header3, sectionalMargin, styled } from "@iventis/styles";
import { useConstant } from "@iventis/utilities";
import { Button } from "@mui/material";
import React, { useMemo } from "react";
import { Route, Routes, useParams } from "react-router";
import { CadApiContext } from "@iventis/cad-georeferencer/src/context/cad-api-context";
import { RepoContainer } from "./assets-repo-template";
import { useProjectSelector } from "./project-selector";
import { createSitemapContext } from "./sitemap-api-functions";
import { useSitemapRepositoryRouting } from "../hooks/use-sitemap-repository-routing";
import { SitemapTree } from "./sitemap-tree";
import { createCadApiContext } from "./cad-api-functions";
import { AdminSitemapVersionCopyForm } from "./admin-sitemap-version-copy";

export const SitemapRepository = () => {
    const { isProjectSelected, projectName, embeddedSelector, instanceName, projectId, switchProjectCallback } = useProjectSelector();

    return (
        <RepoContainer>
            <div className="header-row">
                <StyledHeader>
                    <span>Sitemaps</span> {isProjectSelected && <span>({projectName})</span>}
                </StyledHeader>
                {isProjectSelected && (
                    <Button onClick={switchProjectCallback} variant="text">
                        Switch project
                    </Button>
                )}
            </div>
            {isProjectSelected ? <SitemapRepositoryRouting instanceName={instanceName} projectId={projectId} /> : embeddedSelector}
        </RepoContainer>
    );
};

const SitemapRepositoryRouting = ({ instanceName, projectId }: { instanceName: string; projectId: string }) => {
    const { projectName } = useProjectSelector();
    const sitemapContextValue = useConstant<ISitemapApiContext>(() => createSitemapContext(instanceName, projectId));
    const sitemapRoutingContextValue = useSitemapRepositoryRouting();

    const copyComponentRenderer: CopyComponentRenderer = (internal: SitemapVersionCopyProps) => (
        <AdminSitemapVersionCopyForm
            instanceName={instanceName}
            projectId={projectId}
            sourceSitemapId={internal.sourceSitemapId}
            sourceSitemapVersionId={internal.sourceSitemapVersionId}
            close={internal.close}
        />
    );

    return (
        <SitemapApiContext.Provider value={sitemapContextValue}>
            <SitemapRoutingContext.Provider value={sitemapRoutingContextValue}>
                <StyledContainer>
                    <Routes>
                        <Route path="/" element={<SitemapTree projectId={projectId} instanceName={instanceName} />} />
                        <Route path="/:sitemapId" element={<SitemapForm copyComponent={copyComponentRenderer} projectName={projectName} />} />
                        <Route path="/:sitemapId/:sitemapVersionId" element={<SitemapVersionForm />} />
                        <Route path="/:sitemapId/:sitemapVersionId/:sitemapVersionLevelId" element={<CadApiContextProviderRoute />} />
                    </Routes>
                </StyledContainer>
            </SitemapRoutingContext.Provider>
        </SitemapApiContext.Provider>
    );
};

const CadApiContextProviderRoute = () => {
    const { sitemapVersionLevelId } = useParams();
    const { projectName } = useProjectSelector();
    const cadApiContextValue = useMemo(() => createCadApiContext(sitemapVersionLevelId), [sitemapVersionLevelId]);
    return (
        <CadApiContext.Provider value={cadApiContextValue}>
            <SitemapVersionLevelForm projectName={projectName} />
        </CadApiContext.Provider>
    );
};

const StyledHeader = styled(Header3)`
    align-self: start;
`;

const StyledContainer = styled.div`
    margin-top: ${sectionalMargin};
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: 0;
`;
